import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { roleColor } from "../data/roleColors";
import { buildAssignmentRoster } from "../services/utilization";
import { positionBucket, roleFamily } from "../services/roles";
import "../styles.css";

function getInitials(name) { return (name || "").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(); }

// Two major groups for this page. PEs plan with the PM side and FEs with the
// field side (same split as roleFamily); QA/QC sits on the field side here.
// positionBucket lumps PEs and FEs together, so FEs get their own bucket.
const SIDES = [
  { key: "pm", label: "PM side", buckets: ["Senior Project Managers", "Project Managers", "Assistant Project Managers", "Project Engineers"] },
  { key: "field", label: "Field side", buckets: ["Senior Superintendents", "Superintendents", "Finish Superintendents", "Assistant Superintendents", "Field Engineers", "QA/QC Project Engineers"] },
  { key: "other", label: "Other", buckets: ["Other"] },
];
function pageBucket(role) {
  const b = positionBucket(role);
  return b === "Project Engineers" && roleFamily(role) === "supt" ? "Field Engineers" : b;
}
// Short labels for the chart axis.
const SHORT = {
  "Senior Project Managers": "Sr. PM", "Project Managers": "PM", "Assistant Project Managers": "APM", "Project Engineers": "PE",
  "Senior Superintendents": "Sr. Supt", "Superintendents": "Supt", "Finish Superintendents": "Finish", "Assistant Superintendents": "Asst. Supt",
  "Field Engineers": "FE", "QA/QC Project Engineers": "QA/QC", "Other": "Other",
};

// Vertical stacked bars per position, grouped PM side | Field side.
// Hand-built SVG (no chart library): active on the bottom, prospective, bench on top.
// Drawn at the card's real pixel width (measured), so bars spread across wide
// screens instead of a small fixed-size chart floating in empty space.
function StaffingBars({ byPosition }) {
  const wrapRef = useRef(null);
  const [W, setW] = useState(960);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setW(Math.max(360, Math.round(entry.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const groups = SIDES.map(s => ({ ...s, cols: s.buckets.filter(b => byPosition[b]?.total) })).filter(g => g.cols.length);
  const nCols = groups.reduce((a, g) => a + g.cols.length, 0);
  const H = 320, padT = 30, padB = 62, padL = 34, padR = 8, groupGap = 40;
  const innerH = H - padT - padB;
  const base = H - padB;
  const slot = (W - padL - padR - groupGap * (groups.length - 1)) / Math.max(1, nCols);
  const barW = Math.max(18, Math.min(88, slot * 0.58));
  const fullNames = slot >= 175; // room for "Assistant Superintendents" without colliding
  const max = Math.max(1, ...groups.flatMap(g => g.cols.map(b => byPosition[b].total)));
  const tickStep = Math.max(1, Math.ceil(max / 4));
  const top = tickStep * Math.ceil(max / tickStep);
  const y = (v) => (v / top) * innerH;

  let cursor = padL;
  const layout = groups.map((g, gi) => {
    if (gi > 0) cursor += groupGap;
    const cols = g.cols.map(b => { const x = cursor + (slot - barW) / 2; cursor += slot; return { b, x }; });
    return { ...g, cols, x0: cols[0].x, x1: cols[cols.length - 1].x + barW };
  });

  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", maxWidth: "100%" }} role="img" aria-label="Staffing by position">
        {Array.from({ length: top / tickStep + 1 }, (_, i) => i * tickStep).map(v => (
          <g key={v}>
            <line x1={padL} x2={W - padR} y1={base - y(v)} y2={base - y(v)} stroke={v === 0 ? "var(--border-mid)" : "var(--border)"} strokeDasharray={v === 0 ? undefined : "3 4"} />
            <text x={padL - 8} y={base - y(v) + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)">{v}</text>
          </g>
        ))}
        {layout.map((g, gi) => (
          <g key={g.key}>
            {gi > 0 && <line x1={g.x0 - (slot - barW) / 2 - groupGap / 2} x2={g.x0 - (slot - barW) / 2 - groupGap / 2} y1={padT - 12} y2={H - 8} stroke="var(--border-mid)" strokeDasharray="4 4" />}
            {g.cols.map(({ b, x }) => {
              const s = byPosition[b];
              const segs = [["active", s.active, "var(--green)", "#fff"], ["prospective", s.prospective, "var(--gold)", "var(--navy-dark)"], ["bench", s.bench, "var(--red)", "#fff"]];
              let yTop = base;
              const multi = segs.filter(([, n]) => n > 0).length > 1; // one color = the total already says it
              return (
                <g key={b}>
                  <title>{`${b}: ${s.active} active · ${s.prospective} prospective · ${s.bench} bench`}</title>
                  {segs.map(([k, n, color, ink]) => {
                    if (!n) return null;
                    const h = y(n); yTop -= h;
                    return (
                      <g key={k}>
                        <rect x={x} y={yTop} width={barW} height={Math.max(0, h - 1.5)} fill={color} rx="3" />
                        {multi && h >= 18 && <text x={x + barW / 2} y={yTop + h / 2 + 4} textAnchor="middle" fontSize="12" fontWeight="700" fill={ink}>{n}</text>}
                      </g>
                    );
                  })}
                  <text x={x + barW / 2} y={yTop - 8} textAnchor="middle" fontSize="15" fontWeight="800" fill="var(--text-primary)">{s.total}</text>
                  <text x={x + barW / 2} y={base + 20} textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--text-secondary)">{fullNames ? b : (SHORT[b] || b)}</text>
                </g>
              );
            })}
            <text x={(g.x0 + g.x1) / 2} y={H - 12} textAnchor="middle" fontSize="11" fontWeight="700" letterSpacing="1.5" fill="var(--text-muted)">{g.label.toUpperCase()}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

const TONE_BG = {
  green: "rgba(61,184,122,0.14)",
  yellow: "rgba(217,143,0,0.16)",
  gold: "rgba(217,143,0,0.10)",
  red: "rgba(224,90,74,0.15)",
  muted: "rgba(120,128,140,0.10)",
};

const fmtMon = (d) => {
  if (!d) return "?";
  const dt = new Date(d);
  if (isNaN(dt) || dt.getFullYear() < 2000) return "?"; // guard empty/epoch dates
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const validDate = (d) => d && !isNaN(new Date(d)) && new Date(d).getFullYear() >= 2000;

function UtilBar({ activeNow, potentialNow, peak }) {
  const scale = Math.max(100, peak);
  const aw = (activeNow / scale) * 100;
  const pw = (potentialNow / scale) * 100;
  const overLine = (100 / scale) * 100;
  return (
    <div style={{ position: "relative", height: 12, background: "rgba(0,0,0,0.18)", borderRadius: 6, overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${aw}%`, background: "var(--green)" }} />
      <div style={{ position: "absolute", left: `${aw}%`, top: 0, height: "100%", width: `${pw}%`, background: "repeating-linear-gradient(45deg, var(--gold), var(--gold) 5px, rgba(255,255,255,0.35) 5px, rgba(255,255,255,0.35) 10px)" }} />
      {peak > 100 && <div style={{ position: "absolute", left: `${overLine}%`, top: 0, height: "100%", width: 2, background: "var(--red)" }} title="100% line" />}
    </div>
  );
}

function RosterCard({ person, util, state, overcommitted, bench }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sorted = [...util.rows].sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0));
  let prevEnd = null;
  return (
    <Link to={`/people/${person.Id}`} style={{ display: "block", padding: "14px 18px", textDecoration: "none", color: "inherit", borderRadius: "var(--radius)", border: `1px solid ${state.color}`, borderLeft: `5px solid ${state.color}`, background: TONE_BG[state.tone] || "var(--bg-card)" }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div className="avatar" style={{ width: 40, height: 40, fontSize: 13, flexShrink: 0, background: roleColor(person.CompanyRole) }}>{getInitials(person.FullName)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{person.FullName}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.4px" }}>{person.CompanyRole}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 800, padding: "4px 12px", borderRadius: 999, background: state.color, color: "#fff", whiteSpace: "nowrap", flexShrink: 0 }}>{state.label}</span>
        <div style={{ textAlign: "right", minWidth: 92, flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: overcommitted ? "var(--red)" : "var(--text-primary)", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }} title="Peak concurrent utilization">
            {overcommitted && <Icon name="alert" size={16} title="Overlapping jobs exceed 100%" />}
            {util.peakUtil}%
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }} title="Utilization as of today">{util.currentUtil}% now · peak</div>
        </div>
      </div>

      {/* Utilization bar (full width) */}
      <div style={{ marginTop: 12 }}>
        <UtilBar activeNow={util.activeNowUtil} potentialNow={util.potentialNowUtil} peak={util.peakUtil} />
      </div>

      {/* Assignment lines (full width, roomy) */}
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
        {bench ? <span style={{ color: "var(--text-muted)" }}>No current assignments — available on the bench.</span> : sorted.map((r, i) => {
          const sValid = validDate(r.start);
          const startsFuture = sValid && new Date(r.start) > today;
          const tag = startsFuture ? "Prospective home" : (r.status === "Potential" ? "Pipeline" : "Current");
          const tagColor = startsFuture ? "var(--gold-text)" : (r.status === "Potential" ? "var(--gold-text)" : "var(--green)");
          const gapEl = (prevEnd && sValid && new Date(prevEnd) < new Date(r.start))
            ? <div key={`g${i}`} style={{ color: "var(--text-muted)", fontStyle: "italic", paddingLeft: 4 }}>↳ open {fmtMon(prevEnd)} → {fmtMon(r.start)}</div>
            : null;
          if (validDate(r.end)) prevEnd = r.end;
          return (
            <React.Fragment key={i}>
              {gapEl}
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <b style={{ color: r.status === "Active" ? "var(--green)" : "var(--gold-text)", fontSize: 13 }}>{r.jobName}</b>
                <span style={{ fontSize: 9, fontWeight: 800, color: "#fff", background: tagColor, padding: "1px 7px", borderRadius: 999, textTransform: "uppercase", letterSpacing: "0.4px" }}>{tag}</span>
                <span style={{ color: "var(--text-muted)" }}>{r.role} · {r.alloc}% · {fmtMon(r.start)} → {fmtMon(r.end)}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </Link>
  );
}

const TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "On active job" },
  { key: "pipeline", label: "Pipeline only" },
  { key: "bench", label: "On bench" },
  { key: "over", label: "Overcommitted" },
];

export default function Assignments() {
  const { people, projects, teamMembers, loading, error } = useData();
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  // Collapsed sections, keyed "side:pm" or "pos:Project Managers".
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = (key) => setCollapsed(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const roster = useMemo(
    () => buildAssignmentRoster(people || [], teamMembers || [], projects || []).filter(r => r.benchEligible),
    [people, teamMembers, projects]
  );

  const counts = useMemo(() => ({
    active: roster.filter(r => r.state.key === "active").length,
    pipeline: roster.filter(r => r.state.key === "likely" || r.state.key === "potential").length,
    bench: roster.filter(r => r.bench).length,
    over: roster.filter(r => r.overcommitted).length,
  }), [roster]);

  const q = search.toLowerCase();
  const filtered = roster.filter(r => {
    if (q && !r.person.FullName.toLowerCase().includes(q) && !(r.person.CompanyRole || "").toLowerCase().includes(q)) return false;
    if (tab === "active") return r.state.key === "active";
    if (tab === "pipeline") return r.state.key === "likely" || r.state.key === "potential";
    if (tab === "bench") return r.bench;
    if (tab === "over") return r.overcommitted;
    return true;
  });

  // Group by side → position
  const grouped = {};
  filtered.forEach(r => { const b = pageBucket(r.person.CompanyRole); (grouped[b] = grouped[b] || []).push(r); });
  const sideGroups = SIDES
    .map(s => ({ ...s, positions: s.buckets.filter(b => grouped[b]?.length) }))
    .filter(s => s.positions.length)
    .map(s => ({ ...s, count: s.positions.reduce((a, b) => a + grouped[b].length, 0) }));
  const allKeys = sideGroups.flatMap(s => [`side:${s.key}`, ...s.positions.map(b => `pos:${b}`)]);
  const allCollapsed = allKeys.length > 0 && allKeys.every(k => collapsed.has(k));

  // Position breakdown (active / prospective / bench) over the FULL roster.
  const byPosition = {};
  roster.forEach(r => {
    const b = pageBucket(r.person.CompanyRole);
    const s = byPosition[b] = byPosition[b] || { active: 0, prospective: 0, bench: 0, total: 0 };
    if (r.state.key === "active") s.active++;
    else if (r.state.key === "likely" || r.state.key === "potential") s.prospective++;
    else s.bench++;
    s.total++;
  });
  const hasPositions = Object.keys(byPosition).length > 0;

  const pipeline = (projects || []).filter(p => p.Status === "Potential").map(p => ({
    project: p,
    members: (teamMembers || []).filter(m => m.ProjectJobNumber === p.Title),
  }));

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading assignments…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Assignments</div>
          <div className="page-subtitle">Field & PM staffing — who's staffed, on the bench, or overcommitted</div>
        </div>
      </div>

      {/* Summary cards double as filters — click one to filter the roster, click again to clear. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14, marginBottom: 20 }}>
        {[
          { key: "active", n: counts.active, label: "On active jobs", color: "var(--green)" },
          { key: "pipeline", n: counts.pipeline, label: "Pipeline only", color: "var(--gold-text)" },
          { key: "bench", n: counts.bench, label: "On bench", color: "var(--red)" },
          { key: "over", n: counts.over, label: "Overcommitted", color: "var(--red)", caution: true },
        ].map(s => {
          const selected = tab === s.key;
          return (
            <button key={s.key} className={`card stat-click ${selected ? "selected" : ""}`} style={{ padding: 16 }}
              onClick={() => setTab(selected ? "all" : s.key)}
              title={selected ? "Click to show everyone" : `Show only: ${s.label}`}>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color, display: "flex", alignItems: "center", gap: 8 }}>
                {s.caution && s.n > 0 && <Icon name="alert" size={20} />}{s.n}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{s.label}</div>
              <div className="stat-click-hint">{selected ? "✕ Clear filter" : "Click to filter"}</div>
            </button>
          );
        })}
      </div>

      {/* Staffing by position */}
      {hasPositions && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <div className="card-title">Staffing by Position</div>
            <div style={{ display: "flex", gap: 14, fontSize: 11, color: "var(--text-secondary)" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 8, background: "var(--green)", borderRadius: 2 }} /> Active job</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 8, background: "var(--gold)", borderRadius: 2 }} /> Prospective</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 8, background: "var(--red)", borderRadius: 2 }} /> On bench</span>
            </div>
          </div>
          <StaffingBars byPosition={byPosition} />
        </div>
      )}

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 12, color: "var(--text-secondary)", marginBottom: 14 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 14, height: 8, background: "var(--green)", borderRadius: 2 }} /> Active utilization</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 14, height: 8, borderRadius: 2, background: "repeating-linear-gradient(45deg, var(--gold), var(--gold) 4px, rgba(255,255,255,0.4) 4px, rgba(255,255,255,0.4) 8px)" }} /> Pipeline (potential)</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="alert" size={13} style={{ color: "var(--red)" }} /> Over 100% total</span>
      </div>

      <div className="flex items-center gap-12 mb-16" style={{ flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TABS.map(t => (
            <button key={t.key} className={`btn ${tab === t.key ? "btn-primary" : "btn-ghost"}`} style={{ fontSize: 13, padding: "6px 14px" }} onClick={() => setTab(t.key)}>
              {t.label}{t.key === "over" && counts.over > 0 ? ` (${counts.over})` : ""}
            </button>
          ))}
        </div>
        <input className="form-input" style={{ maxWidth: 280, marginLeft: "auto" }} placeholder="Search name or role…" value={search} onChange={e => setSearch(e.target.value)} />
        {allKeys.length > 0 && (
          <button className="btn btn-ghost" style={{ fontSize: 13, padding: "6px 14px", display: "inline-flex", alignItems: "center", gap: 6 }}
            onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(allKeys))}>
            <Icon name={allCollapsed ? "chevrons-down" : "chevrons-up"} size={14} /> {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        )}
      </div>

      {/* Roster: PM side / Field side → position, each level collapsible */}
      {filtered.length === 0 ? (
        <div className="empty"><div className="empty-icon"><Icon name="users" /></div><div className="empty-text">No one matches this filter.</div></div>
      ) : (
        sideGroups.map(side => {
          const sideOpen = !collapsed.has(`side:${side.key}`);
          return (
            <section key={side.key} className="as-side">
              <button className="as-side-head" onClick={() => toggle(`side:${side.key}`)} aria-expanded={sideOpen}>
                <Icon name={sideOpen ? "chevron-down" : "chevron-right"} size={18} />
                <span className="as-side-title">{side.label}</span>
                <span className="as-count">{side.count} {side.count === 1 ? "person" : "people"}</span>
                {!sideOpen && <span className="as-side-summary">{side.positions.map(b => `${grouped[b].length} ${SHORT[b] || b}`).join(" · ")}</span>}
              </button>
              {sideOpen && side.positions.map(bucket => {
                const posOpen = !collapsed.has(`pos:${bucket}`);
                return (
                  <div key={bucket} className="as-pos">
                    <button className="as-pos-head" onClick={() => toggle(`pos:${bucket}`)} aria-expanded={posOpen}>
                      <Icon name={posOpen ? "chevron-down" : "chevron-right"} size={15} />
                      <span>{bucket}</span>
                      <span className="as-count">{grouped[bucket].length}</span>
                    </button>
                    {posOpen && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {grouped[bucket].map(item => <RosterCard key={item.person.Id} {...item} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          );
        })
      )}

      {/* Pipeline staffing */}
      {pipeline.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div className="section-title mb-16">Pipeline Staffing <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)" }}>· prospective jobs you can pre-staff</span></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {pipeline.map(({ project, members }) => (
              <div key={project.Id} className="card" style={{ borderTop: "3px solid var(--gold)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{project.JobName || project.Title}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{project.Title} · pipeline</div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "var(--gold-text)", whiteSpace: "nowrap" }}>{project.Confidence != null ? `${project.Confidence}%` : "—"} conf</span>
                </div>
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                  {members.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Not pre-staffed yet.</div>
                  ) : members.map((m, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                      <div className="avatar" style={{ width: 22, height: 22, fontSize: 9, background: roleColor(m.ProjectRole) }}>{getInitials(m.PersonName)}</div>
                      <span style={{ fontWeight: 600 }}>{m.PersonName}</span>
                      <span style={{ color: "var(--text-muted)" }}>{m.ProjectRole}{m.AllocationPercent != null ? ` · ${m.AllocationPercent}%` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
